import { useCallback, useEffect, useRef, useState } from 'react'
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { find, flatMap, get } from 'lodash'

import { PaginatedResponseResponseThread, ResponseThread } from '@/schema'

/**
 * Hook to progressively fetch paginated comments until a specific comment is found
 * @param commentId - The ID of the comment to find
 * @param queryResult - The infinite query result from useGetThreadsList
 * @param onCommentFound - Callback when comment is found (receives the comment)
 * @param enabled - Whether the hook should run
 * @returns Object with fetching status and found comment
 */
export const useFetchUntilComment = (
  commentId: string | null,
  queryResult: UseInfiniteQueryResult<
    InfiniteData<PaginatedResponseResponseThread>,
    Error
  >,
  onCommentFound?: (comment: ResponseThread) => void,
  onCommentNotFound?: () => void,
  enabled = true
) => {
  const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } =
    queryResult

  const [isFetchingUntilFound, setIsFetchingUntilFound] = useState(false)
  const [foundComment, setFoundComment] = useState<ResponseThread | null>(null)
  // State to store if comment is not found
  const [isCommentNotFound, setIsCommentNotFound] = useState(false)
  const fetchingRef = useRef(false)

  // Check if comment exists in current loaded pages
  const findComment = useCallback(
    (targetCommentId: string): ResponseThread | null => {
      const pages = get(data, 'pages', [])
      const allComments = flatMap(pages, (page) => page.data)
      return (
        find(
          allComments,
          (comment) =>
            comment.id === targetCommentId ||
            comment.thread_id === targetCommentId
        ) || null
      )
    },
    [data]
  )

  useEffect(() => {
    // Call onCommentNotFound if provided
    if (isCommentNotFound) onCommentNotFound?.()
  }, [isCommentNotFound, onCommentNotFound])

  useEffect(() => {
    if (enabled && commentId && !hasNextPage && !isFetching) {
      const comment = findComment(commentId)

      // Set isCommentNotFound to true to call onCommentNotFound if provided
      if (!comment) setIsCommentNotFound(true)
    }
  }, [commentId, enabled, findComment, hasNextPage, isFetching])

  // Progressive fetch logic
  useEffect(() => {
    if (!enabled || !commentId || fetchingRef.current) {
      return
    }

    const comment = findComment(commentId)

    if (comment) {
      // Comment found
      setFoundComment(comment)
      setIsFetchingUntilFound(false)
      fetchingRef.current = false
      onCommentFound?.(comment)
      return
    }

    // Comment not found yet
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      // More pages available, fetch next
      setIsFetchingUntilFound(true)
      fetchingRef.current = true

      fetchNextPage().then(() => {
        fetchingRef.current = false
      })
    } else if (!hasNextPage) {
      // No more pages, comment not found
      setIsFetchingUntilFound(false)
      fetchingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    commentId,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    data?.pages,
  ])

  // Reset when commentId changes
  useEffect(() => {
    if (commentId) {
      setFoundComment(null)
      setIsCommentNotFound(false)
      fetchingRef.current = false
    }
  }, [commentId])

  return {
    isFetchingUntilFound,
    foundComment,
    isSearching: enabled && !!commentId && !foundComment,
  }
}
